// Seção "Equipe Técnica" — gerencia vínculos em operacao.contrato_equipe.
// Acesso: operacional.contrato.visao.editar_equipe_tecnica (admin, diretor,
// coordenadores). Outros perfis veem mas não adicionam/removem.
//
// Modal de adicionar aceita BATCH — múltiplos emails de uma vez aplicando
// o mesmo papel.
// Wave 2 (Antd) — 2026-06-01.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Table, Tag, Modal, Form, Input, Select, Typography,
  Popconfirm, App as AntApp, Empty, Spin, Flex,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { usePerms } from '../../hooks/usePerms';

const { TextArea } = Input;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface Vinculo {
  id: number;
  contrato_id: string;
  usuario_email: string;
  papel: 'gestor' | 'coordenador' | 'responsavel' | 'equipe' | 'observador';
  observacao: string | null;
  adicionado_em: string;
  adicionado_por: string | null;
}

const PAPEIS = [
  { value: 'gestor',      label: 'Gestor' },
  { value: 'coordenador', label: 'Coordenador' },
  { value: 'responsavel', label: 'Responsável' },
  { value: 'equipe',      label: 'Equipe técnica' },
  { value: 'observador',  label: 'Observador (só leitura)' },
] as const;

const CORES_PAPEL: Record<string, string> = {
  gestor:      '#020D7F',
  coordenador: '#0930D0',
  responsavel: '#0A7F4A',
  equipe:      '#B07500',
  observador:  '#888',
};

export function EquipeContrato({ contratoId }: { contratoId: string }) {
  return <AntApp><EquipeContratoInner contratoId={contratoId} /></AntApp>;
}

function EquipeContratoInner({ contratoId }: { contratoId: string }) {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode('operacional.contrato.visao.editar_equipe_tecnica');
  const [vinculos, setVinculos] = useState<Vinculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [emailsInput, setEmailsInput] = useState('');
  const [papelInput, setPapelInput] = useState<Vinculo['papel']>('equipe');
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    try {
      setLoading(true);
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_equipe_contrato', contrato_id: contratoId },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      setVinculos(d.equipe || []);
    } catch (e: any) {
      message.error('Falha ao carregar equipe: ' + (e?.response?.data?.error || e?.message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [contratoId]);

  async function adicionar() {
    const emails = emailsInput
      .split(/[\s,;\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    if (!emails.length) {
      message.warning('Informe pelo menos um email válido');
      return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_adicionar_equipe', contrato_id: contratoId, emails, papel: papelInput },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      const msg = `${d.total_inseridos} adicionado(s)` +
        (d.ignorados?.length ? ` · ${d.ignorados.length} ignorado(s)` : '');
      message.success(msg);
      setEmailsInput('');
      setModalAberto(false);
      await carregar();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Erro');
    } finally {
      setSalvando(false);
    }
  }

  async function remover(v: Vinculo) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_remover_equipe', id: v.id },
        { headers: { Authorization: token } });
      message.success('Vínculo removido');
      await carregar();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message);
    }
  }

  const columns: ColumnsType<Vinculo> = [
    { title: 'Papel', dataIndex: 'papel', key: 'papel', width: 160,
      render: (p: Vinculo['papel']) => {
        const label = PAPEIS.find(x => x.value === p)?.label || p;
        const cor = CORES_PAPEL[p];
        return <Tag style={{ background: cor + '22', color: cor, fontWeight: 600, border: 'none' }}>{label}</Tag>;
      },
    },
    { title: 'Email', dataIndex: 'usuario_email', key: 'email',
      render: (e: string) => <Typography.Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{e}</Typography.Text>,
    },
    { title: 'Desde', dataIndex: 'adicionado_em', key: 'desde', width: 100,
      render: (d: string) => <Typography.Text type="secondary" style={{ fontSize: 10 }}>
        {d ? new Date(d).toLocaleDateString('pt-BR') : '—'}
      </Typography.Text>,
    },
    ...(podeEditar ? [{
      title: '', key: 'acoes', width: 50, align: 'right' as const,
      render: (_: any, v: Vinculo) => (
        <Popconfirm title={`Remover ${v.usuario_email}?`}
          okText="Remover" okButtonProps={{ danger: true }}
          onConfirm={() => remover(v)}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Remover" />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <Card size="small" style={{ marginTop: 18 }}
      title={<Typography.Text strong style={{ color: '#020D7F', fontSize: 13 }}>
        <TeamOutlined /> Equipe Técnica ({vinculos.length})
      </Typography.Text>}
      extra={podeEditar && (
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setModalAberto(true)}>
          Adicionar (batch)
        </Button>
      )}>
      {loading ? (
        <Flex justify="center" style={{ padding: 20 }}><Spin /></Flex>
      ) : vinculos.length === 0 ? (
        <Empty description={podeEditar ? 'Nenhum membro vinculado. Clique em "Adicionar" pra começar.' : 'Solicite ao gestor do contrato.'} />
      ) : (
        <Table size="small" rowKey="id" columns={columns} dataSource={vinculos} pagination={false} />
      )}

      <Modal open={modalAberto} onCancel={() => !salvando && setModalAberto(false)}
        title={<Typography.Title level={5} style={{ margin: 0, color: '#020D7F' }}>Adicionar membros à equipe</Typography.Title>}
        confirmLoading={salvando} okText="Adicionar"
        onOk={adicionar} width={500}>
        <Form layout="vertical" disabled={salvando}>
          <Form.Item label={<Typography.Text strong style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>
            Emails (um por linha, ou separados por vírgula)
          </Typography.Text>}>
            <TextArea value={emailsInput} onChange={e => setEmailsInput(e.target.value)}
              placeholder={'igor@hydrodataeng.com.br\nrenata@hydrodataeng.com.br\nrodrigo@hydrodataeng.com.br'}
              autoSize={{ minRows: 4, maxRows: 8 }}
              style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <Form.Item label={<Typography.Text strong style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>
            Papel (aplicado a todos os emails acima)
          </Typography.Text>}>
            <Select value={papelInput} onChange={(v) => setPapelInput(v)}
              options={PAPEIS.map(p => ({ value: p.value, label: p.label }))} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
